import { defineStore } from 'pinia';
import KanbanBoardsAPI from 'dashboard/api/kanbanBoards';

// Kanban boards — backed by the Rails API (/api/v1/accounts/:id/kanban_boards).
// Board layout + the column→account-label links are stored in the board's
// `columns` jsonb; the CARDS and label transitions still operate on real
// contacts/labels (handled in the views). Column edits PATCH the whole board.

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export const useKanbanStore = defineStore('kanban', {
  state: () => ({
    boards: [],
  }),
  getters: {
    list: state => state.boards,
    getBoard: state => boardId => state.boards.find(b => b.id === boardId),
  },
  actions: {
    async fetch() {
      const { data } = await KanbanBoardsAPI.get();
      this.boards = (data.payload || []).map(b => ({
        ...b,
        columns: b.columns || [],
      }));
    },
    async createBoard(name) {
      const { data } = await KanbanBoardsAPI.create({
        name: name?.trim() || 'Nuevo tablero',
        columns: [],
      });
      this.boards = [...this.boards, { ...data.payload, columns: data.payload.columns || [] }];
      return data.payload;
    },
    async deleteBoard(boardId) {
      await KanbanBoardsAPI.delete(boardId);
      this.boards = this.boards.filter(b => b.id !== boardId);
    },
    async addColumn(boardId, { title, labelTitle = null, labelColor = null } = {}) {
      const board = this.getBoard(boardId);
      if (!board) return null;
      const column = {
        id: uid(),
        title: title?.trim() || 'Columna',
        labelTitle,
        labelColor,
      };
      await this.persistColumns(board, [...board.columns, column]);
      return column;
    },
    async updateColumn(boardId, columnId, patch) {
      const board = this.getBoard(boardId);
      if (!board) return;
      const columns = board.columns.map(c =>
        c.id === columnId ? { ...c, ...patch } : c
      );
      await this.persistColumns(board, columns);
    },
    async removeColumn(boardId, columnId) {
      const board = this.getBoard(boardId);
      if (!board) return;
      await this.persistColumns(
        board,
        board.columns.filter(c => c.id !== columnId)
      );
    },
    async persistColumns(board, columns) {
      const { data } = await KanbanBoardsAPI.update(board.id, { columns });
      this.boards = this.boards.map(b =>
        b.id === board.id ? { ...data.payload, columns: data.payload.columns || [] } : b
      );
    },
  },
});
